/**
 * service-financial-services client.
 *
 * Citizen-facing (via /v1/wallet/*):
 *   GET    /v1/wallet/wallets                            — list wallets (paginated, filter)
 *   GET    /v1/wallet/wallets/:id                        — fetch one wallet
 *   POST   /v1/wallet/wallets/customers/:cid/get-or-create
 *   POST   /v1/wallet/wallets/:id/{suspend|unsuspend|close|promote-tier}
 *   GET    /v1/wallet/transactions/:id                   — fetch one txn
 *   GET    /v1/wallet/wallets/:walletId/transactions     — list txns for a wallet
 *   POST   /v1/wallet/credits | /v1/wallet/debits | /v1/wallet/transfers
 *   POST   /v1/wallet/transactions/:id/reverse
 *   GET    /v1/wallet/bill-providers                     — list providers
 *   GET    /v1/wallet/bill-providers/:code               — fetch one
 *   POST   /v1/wallet/bill-payments                      — pay a bill
 *   GET    /v1/wallet/bill-payments/:id                  — fetch one
 *
 * Operator back-office (via /v1/financial-services/*) — added in slice 9:
 *   GET    /v1/financial-services/dashboard              — KPI overview
 *   GET    /v1/financial-services/bill-payments          — bill-payment ledger
 *   GET    /v1/financial-services/transfers              — P2P transfer ledger
 *   GET    /v1/financial-services/fraud-alerts           — fraud alert queue
 *   POST   /v1/financial-services/fraud-alerts/:id/disposition — mark alert
 */
import { env } from '../env';

import { request } from './client';

const baseUrl = (): string => env.serviceFinancialServicesBaseUrl;

// ─── Wallet ───────────────────────────────────────────────────────────────

export type WalletStatus = 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
export type KycTier = 'TIER_1' | 'TIER_2' | 'TIER_3';

export interface Wallet {
  readonly id: string;
  readonly walletNumber: string;
  readonly tenantId: string;
  readonly customerId: string;
  readonly currency: string;
  readonly status: WalletStatus;
  readonly kycTier: KycTier;
  /** DECIMAL stored as string. */
  readonly balanceCached: string;
  /** DECIMAL stored as string. */
  readonly dailyInflowToday: string;
  readonly dailyInflowDate: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListWalletsResponse {
  readonly items: readonly Wallet[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ListWalletsQuery {
  readonly status?: WalletStatus;
  readonly kycTier?: KycTier;
  readonly customerId?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

export async function listWallets(q: ListWalletsQuery = {}): Promise<ListWalletsResponse> {
  return request<ListWalletsResponse>(baseUrl(), '/v1/wallet/wallets', {
    method: 'GET',
    query: {
      status: q.status,
      kycTier: q.kycTier,
      customerId: q.customerId,
      page: q.page,
      pageSize: q.pageSize,
    },
  });
}

export async function getWallet(id: string): Promise<Wallet> {
  return request<Wallet>(baseUrl(), `/v1/wallet/wallets/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

// ─── Wallet transactions ──────────────────────────────────────────────────

export type TxnSide = 'CREDIT' | 'DEBIT';
export type TxnStatus = 'PENDING' | 'POSTED' | 'REVERSED' | 'FAILED';
export type TxnAction =
  | 'TOPUP'
  | 'PAYMENT'
  | 'TRANSFER'
  | 'WITHDRAWAL'
  | 'REFUND'
  | 'AIRTIME'
  | 'BILL'
  | 'DISBURSEMENT';

export interface WalletTransaction {
  readonly id: string;
  readonly txnNumber: string;
  readonly walletId: string;
  readonly side: TxnSide;
  readonly action: TxnAction;
  /** DECIMAL stored as string. */
  readonly amount: string;
  readonly currency: string;
  /** DECIMAL stored as string. */
  readonly balanceAfter: string;
  readonly status: TxnStatus;
  readonly failureReason: string | null;
  readonly counterRef: string | null;
  readonly metadata: unknown;
  readonly initiatedBy: string;
  readonly initiatedAt: string;
  readonly completedAt: string | null;
}

export interface ListWalletTransactionsResponse {
  readonly items: readonly WalletTransaction[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ListTxnQuery {
  readonly side?: TxnSide;
  readonly status?: TxnStatus;
  readonly action?: TxnAction;
  readonly page?: number;
  readonly pageSize?: number;
}

export async function listWalletTransactions(
  walletId: string,
  q: ListTxnQuery = {},
): Promise<ListWalletTransactionsResponse> {
  return request<ListWalletTransactionsResponse>(
    baseUrl(),
    `/v1/wallet/wallets/${encodeURIComponent(walletId)}/transactions`,
    {
      method: 'GET',
      query: {
        side: q.side,
        status: q.status,
        action: q.action,
        page: q.page,
        pageSize: q.pageSize,
      },
    },
  );
}

// ─── Wallet credit (staff-side top-up on a customer's behalf) ────────────────

export interface RecordCreditRequest {
  readonly walletId: string;
  /** TOPUP_CARD | TOPUP_TRANSFER | DISBURSEMENT — staff top-ups use TOPUP_TRANSFER. */
  readonly action: 'TOPUP_CARD' | 'TOPUP_TRANSFER' | 'DISBURSEMENT';
  /** DECIMAL string, e.g. "5000.00". */
  readonly amount: string;
  readonly currency: string;
  readonly counterRef?: string;
  readonly idempotencyKey?: string;
  readonly metadata?: Record<string, unknown>;
}

/** POST /v1/wallet/credits — record a CREDIT against a wallet. Requires
 *  WalletTransaction/credit. Staff use this to top up a citizen wallet at the
 *  counter (cash-in) or to record an inbound bank transfer. */
export async function recordWalletCredit(
  body: RecordCreditRequest,
): Promise<WalletTransaction> {
  return request<WalletTransaction>(baseUrl(), '/v1/wallet/credits', {
    method: 'POST',
    body,
  });
}

// ─── Bill providers ───────────────────────────────────────────────────────

export interface BillProvider {
  readonly id: string;
  readonly code: string;
  readonly category: string;
  readonly displayName: string;
  readonly inputSchema: unknown;
  readonly active: boolean;
}

export interface ListBillProvidersResponse {
  readonly items: readonly BillProvider[];
}

export async function listBillProviders(active?: boolean): Promise<ListBillProvidersResponse> {
  return request<ListBillProvidersResponse>(baseUrl(), '/v1/wallet/bill-providers', {
    method: 'GET',
    query: active === undefined ? {} : { active: active ? 'true' : 'false' },
  });
}

/**
 * Format a service-supplied DECIMAL string as Naira.
 *
 * service-financial-services serialises `WalletTransaction.amount` and
 * `Wallet.balanceCached` as DECIMAL strings (not minor units). Phase 4b's
 * franchise pages adopted the same pattern — keep the helper signature
 * identical so callers stay consistent.
 */
export function formatNgn(decimal: string): string {
  const n = Number.parseFloat(decimal);
  if (!Number.isFinite(n)) return `₦${decimal}`;
  return `₦${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

// ─── Operator back-office (/v1/financial-services/*) ─────────────────────

export interface FinancialDashboardKpis {
   
  readonly activeWallets: number;
   
  readonly todayTransactionCount: number;
   
  readonly flaggedAlertsCount: number;
}

export async function getFinancialDashboard(): Promise<FinancialDashboardKpis> {
  return request<FinancialDashboardKpis>(baseUrl(), '/v1/financial-services/dashboard', {
    method: 'GET',
  });
}

// ─── Bill payments ledger ─────────────────────────────────────────────────

export type BillPaymentStatus = 'PENDING' | 'COMPLETED' | 'FAILED';

export interface BillPaymentLedgerItem {
  readonly id: string;
  readonly walletTransactionId: string;
  readonly providerCode: string;
  readonly providerRef: string | null;
  readonly status: string;
  readonly failureReason: string | null;
}

export interface ListBillPaymentsLedgerResponse {
  readonly items: readonly BillPaymentLedgerItem[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ListBillPaymentsLedgerQuery {
  readonly status?: BillPaymentStatus;
  readonly page?: number;
  readonly pageSize?: number;
}

export async function listBillPaymentsLedger(
  q: ListBillPaymentsLedgerQuery = {},
): Promise<ListBillPaymentsLedgerResponse> {
  return request<ListBillPaymentsLedgerResponse>(
    baseUrl(),
    '/v1/financial-services/bill-payments',
    {
      method: 'GET',
      query: {
        status: q.status,
        page: q.page,
        pageSize: q.pageSize,
      },
    },
  );
}

// ─── Transfers ledger ─────────────────────────────────────────────────────

export interface ListTransfersLedgerResponse {
  readonly items: readonly WalletTransaction[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ListTransfersLedgerQuery {
  readonly page?: number;
  readonly pageSize?: number;
}

export async function listTransfersLedger(
  q: ListTransfersLedgerQuery = {},
): Promise<ListTransfersLedgerResponse> {
  return request<ListTransfersLedgerResponse>(
    baseUrl(),
    '/v1/financial-services/transfers',
    {
      method: 'GET',
      query: { page: q.page, pageSize: q.pageSize },
    },
  );
}

// ─── POS daily-volume rollups ────────────────────────────────────────────

export interface DailyVolumeMethodBreakdown {
   
  readonly txnCount: number;
  /** DECIMAL string in NGN. */
  readonly totalAmount: string;
}

export interface PosDailyVolumeBucket {
  /** YYYY-MM-DD (UTC). */
  readonly date: string;
   
  readonly txnCount: number;
  /** DECIMAL string in NGN — sum across all methods for this date. */
  readonly totalAmount: string;
  readonly byMethod: {
    readonly CASH: DailyVolumeMethodBreakdown;
    readonly POS: DailyVolumeMethodBreakdown;
    readonly TRANSFER: DailyVolumeMethodBreakdown;
  };
}

export interface ListPosDailyVolumesResponse {
  readonly items: readonly PosDailyVolumeBucket[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  /** DECIMAL sum across all returned buckets. */
  readonly periodTotal: string;
   
  readonly periodTxnCount: number;
}

export interface ListPosDailyVolumesQuery {
  /** ISO date-time. */
  readonly from?: string;
  /** ISO date-time. */
  readonly to?: string;
  readonly outletCode?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

export async function getPosDailyVolumes(
  q: ListPosDailyVolumesQuery = {},
): Promise<ListPosDailyVolumesResponse> {
  return request<ListPosDailyVolumesResponse>(
    baseUrl(),
    '/v1/financial-services/pos/daily-volumes',
    {
      method: 'GET',
      query: {
        from: q.from,
        to: q.to,
        outletCode: q.outletCode,
        page: q.page,
        pageSize: q.pageSize,
      },
    },
  );
}

// ─── Fraud alerts ─────────────────────────────────────────────────────────

export type FraudAlertStatus = 'NEW' | 'REVIEWED' | 'DISMISSED' | 'CONFIRMED';
export type FraudAlertSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface FraudAlert {
  readonly id: string;
  readonly walletTransactionId: string | null;
  readonly ruleId: string;
  readonly severity: FraudAlertSeverity;
  readonly status: FraudAlertStatus;
  readonly dispositionBy: string | null;
  readonly dispositionAt: string | null;
  readonly dispositionNotes: string | null;
  readonly metadata: unknown;
  readonly createdAt: string;
}

export interface ListFraudAlertsResponse {
  readonly items: readonly FraudAlert[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ListFraudAlertsQuery {
  readonly status?: FraudAlertStatus;
  readonly page?: number;
  readonly pageSize?: number;
}

export async function listFraudAlerts(
  q: ListFraudAlertsQuery = {},
): Promise<ListFraudAlertsResponse> {
  return request<ListFraudAlertsResponse>(
    baseUrl(),
    '/v1/financial-services/fraud-alerts',
    {
      method: 'GET',
      query: {
        status: q.status,
        page: q.page,
        pageSize: q.pageSize,
      },
    },
  );
}

export interface DispositionFraudAlertRequest {
  readonly status: 'REVIEWED' | 'DISMISSED' | 'CONFIRMED';
  readonly notes?: string;
}

export async function dispositionFraudAlert(
  id: string,
  dto: DispositionFraudAlertRequest,
): Promise<FraudAlert> {
  return request<FraudAlert>(
    baseUrl(),
    `/v1/financial-services/fraud-alerts/${encodeURIComponent(id)}/disposition`,
    {
      method: 'POST',
      body: dto,
    },
  );
}
